<template>

    <div class="container">

        <app-head title="Customers" />

        <div class="d-flex justify-content-start align-items-center mb-3 gap-3">
            <h1 class="">{{ __('Kunden') }}</h1>
            <div class="">
                <inertia-link class="btn btn-primary" :href="route('admin.customers.create')">
                    {{ __('Create') }}
                </inertia-link>
            </div>
        </div>

        <div class="d-flex mb-3">
            <search-filter v-model="form.search" class="" @reset="reset">
                <select-input label="Status" v-model="form.status">
                    <option :value="null">{{ __('Veröffentlicht') }}</option>
                    <option value="trashed">{{ __('Papierkorb') }}</option>
                </select-input>
                <select-input label="customer_group_id" v-model="form.customer_group_id">
                    <option :value="null">{{ __('All') }}</option>
                    <option :value="customerGroup.id" v-for="customerGroup in customerGroups">{{ customerGroup.name }}</option>
                </select-input>
            </search-filter>
        </div>

        <div class="d-flex justify-content-between">
            <results :meta="customers.meta" />
            <div>
                <i class="bi bi-download"></i>
                {{ __('Export') }}
                <a :href="getExportUrl('xlsx')">
                    {{ __('XLSX') }}
                </a>
                /
                <a :href="getExportUrl('csv')">
                    {{ __('CSV') }}
                </a>
                /
                <a :href="getExportUrl('ods')">
                    {{ __('ODS') }}
                </a>
            </div>
        </div>

        <div class="card">
            <data-table :items="customers.data"
                        :links="customers.meta.links"
                        :columns="columns"
                        >
                <data-table-row v-for="customer in customers.data" :key="customer.id">
                    <td>
                        {{ customer.id }}
                    </td>
                    <td>
                        <div v-if="customer.deleted_at">
                            {{ customer.name }}
                        </div>
                        <inertia-link class="" :href="route('admin.customers.edit', customer)" v-else>
                            {{ customer.name }}
                        </inertia-link>
                        <div class="small">
                            <i class="bi bi-envelope"></i>
                            <a :href="'mailto:' + customer.email">
                                {{ customer.email }}
                            </a>
                        </div>
                    </td>
                    <td>
                        {{ customer.customer_number }}
                    </td>
                    <td>
                        <div v-if="customer.customer_group">
                            {{ customer.customer_group.name }}
                        </div>
                        <div v-else>
                            -
                        </div>
                    </td>
                    <td>
                        <inertia-link class="" :href="route('admin.orders.index', {customer_id: customer.id})">
                            {{ customer.total_orders || 0 | localizeNumber }}
                        </inertia-link>
                    </td>
                    <td>
                        {{ customer.revenue || 0 | localizeAmount }}
                    </td>
                    <td>
                        {{ customer.average_order_sum || 0 | localizeAmount }}
                    </td>
                    <td class="text-end py-0">

                        <template v-if="customer.deleted_at">
                            <button type="button" class="btn btn-outline-success" @click="restore(customer)">
                                {{ __('Restore') }}
                            </button>
                            <button type="button" class="btn btn-outline-danger" @click="destroyPermanently(customer)">
                                {{ __('Destroy permanently') }}
                            </button>
                        </template>
                        <div class="dropdown" v-else>
                            <button class="btn btn-outline-secondary" 
                                    type="button" 
                                    :id="`dropdownMenuCustomer-${customer.id}`" 
                                    :title="__('Edit')"
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false">
                                <i class="bi bi-three-dots-vertical"></i>
                                {{ __('Edit') }}
                            </button>
                            <div class="dropdown-menu dropdown-menu-end" :aria-labelledby="`dropdownMenuCustomer-${customer.id}`">
                                <inertia-link :href="route('admin.customers.edit', {customer})" class="dropdown-item">
                                    {{ __('Bearbeiten') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.users.index', {account_id: customer.id})" class="dropdown-item">
                                    {{ __('Users') }}
                                </inertia-link>
                                <inertia-link :href="route('admin.customers.addresses.index', {customer})" class="dropdown-item">
                                    {{ __('Addresses') }}
                                </inertia-link>
                            </div>
                        </div>
                    </td>
                </data-table-row>
            </data-table>
        </div>

    </div>

</template>

<script>

    import DataTable from '@/shared/Admin/DataTable';
    import DataTableRow from '@/shared/Admin/DataTableRow';
    import SearchFilter from '@/shared/Admin/SearchFilter';
    import SelectInput from '@/shared/Inputs/SelectInput';

    import Results from '@/shared/Admin/Results';

    export default {
        props: [
            'filters',
            'customers',
            'customerGroups',
        ],
        components: {
            SearchFilter,
            SelectInput,
            DataTable,
            DataTableRow,
            Results,
        },
        data() {
            return {
                form: {
                    account_type: this.filters.account_type,
                    search: this.filters.search,
                    status: this.filters.status,
                    customer_group_id: this.filters.customer_group_id,
                },
                columns: {
                    id: {label: 'ID', sortable: 'id'},
                    name: {label: 'Name', sortable: 'name'},
                    customer_number: {label: 'Customer Number', sortable: 'customer_number'},
                    customer_group: {label: 'Customer Group'},
                    total_orders: {label: 'Total Orders', sortable: 'total_orders'},
                    revenue: {label: 'Revenue', sortable: 'revenue'},
                    average_order_sum: {label: 'Ø Bestellwert', sortable: 'average_order_sum'},
                },
            }
        },
        watch: {
            form: {
                deep: true,
                handler: _.debounce(function () {
                    this.$inertia.get(this.route('admin.customers.index'), this.form, {preserveState: true})
                }, 300, {trailing: true}),
            },
        },
        methods: {
            reset() {
                this.form = _.mapValues(this.form, () => null)
            },
            destroyPermanently(customer) {
                if (confirm(this.__('Sind Sie sicher?'))) {
                    this.$inertia.delete(this.route('admin.customers.destroy-permanently', customer.id));
                }
            },
            restore(customer) {
                this.$inertia.get(this.route('admin.customers.restore', customer.id));
            },
            getExportUrl(format) {
                return this.route('admin.customers.export', {format: format, ...this.filters});
            }
        },
    }
</script>